/**
 * Turning an audit entry into something a person reads.
 *
 * The log stores machine state — `{"tenant":"esb"}`, twelve sync counters — and
 * the page used to print it as JSON. This module is the one place that knows
 * what each action means, so the table, the expanded view and the tests agree.
 *
 * Every action has a fallback: an action added later without an entry here
 * still renders as a readable list of changed fields, never as JSON.
 */
import { formatDate, formatDateTime } from '@/features/license-assignment/cellState';
import type { AuditEntry } from '@/types/api';

type Json = Record<string, unknown>;

/** Chip colours, by the area of the system an action touches. */
export type AuditTone = 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';

interface ActionInfo {
  label: string;
  tone: AuditTone;
}

export const ACTIONS: Record<string, ActionInfo> = {
  'user.updated': { label: 'Benutzer geändert', tone: 'primary' },
  'user.tenant_changed': { label: 'Mandant gewechselt', tone: 'primary' },
  'user.assigned': { label: 'Bearbeitbar gemacht', tone: 'primary' },
  'user.kickout_requested': { label: 'Löschung angefordert', tone: 'error' },
  'user.kickout': { label: 'Benutzer gelöscht', tone: 'error' },
  'license.enabled': { label: 'Lizenz aktiviert', tone: 'success' },
  'license.disabled': { label: 'Lizenz deaktiviert', tone: 'warning' },
  'license.deleted': { label: 'Lizenz zurückgesetzt', tone: 'warning' },
  'licenses.copied_from_production': { label: 'Lizenzen übernommen', tone: 'success' },
  'override.created': { label: 'Ausnahme erstellt', tone: 'info' },
  'override.updated': { label: 'Ausnahme geändert', tone: 'info' },
  'override.deleted': { label: 'Ausnahme entfernt', tone: 'info' },
  'tenant.updated': { label: 'Mandant bearbeitet', tone: 'secondary' },
  'app.created': { label: 'App angelegt', tone: 'secondary' },
  'app.updated': { label: 'App geändert', tone: 'secondary' },
  'app.enabled': { label: 'App aktiviert', tone: 'secondary' },
  'app.disabled': { label: 'App deaktiviert', tone: 'error' },
  'credential.created': { label: 'Token hinterlegt', tone: 'default' },
  'credential.rotated': { label: 'Token ersetzt', tone: 'default' },
  'credential.deleted': { label: 'Token entfernt', tone: 'default' },
  'sync.completed': { label: 'Synchronisierung', tone: 'default' },
};

export function actionInfo(action: string): ActionInfo {
  return ACTIONS[action] ?? { label: action, tone: 'default' };
}

const FIELD_LABELS: Record<string, string> = {
  tenant: 'Mandant',
  sep_role: 'Rolle',
  username: 'Benutzername',
  user: 'Benutzer',
  app: 'App',
  sep_user_id: 'SEP-ID',
  status: 'Status',
  is_enabled: 'Aktiv',
  valid_from: 'Gültig ab',
  valid_until: 'Gültig bis',
  seats: 'Plätze',
  notes: 'Notiz',
  source: 'Quelle',
  effect: 'Wirkung',
  reason: 'Grund',
  expires_at: 'Läuft ab',
  fingerprint: 'Fingerabdruck',
  key: 'Schlüssel',
  display_name: 'Anzeigename',
  created_in_sep: 'In SEP neu angelegt',
  sep_accepted: 'Von SEP bestätigt',
  target: 'Ziel',
  created: 'Neu',
  updated: 'Geändert',
  deleted: 'Entfernt',
  missing_tenants: 'Fehlende Mandanten',
};

export function fieldLabel(key: string): string {
  return FIELD_LABELS[key] ?? key;
}

const EFFECTS: Record<string, string> = { allow: 'freigegeben', deny: 'gesperrt' };

const ISO_DATE = /^\d{4}-\d{2}-\d{2}(T[\d:.]+(Z|[+-]\d{2}:?\d{2})?)?$/;

export function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'ja' : 'nein';
  if (Array.isArray(value)) return value.length ? value.map(formatValue).join(', ') : '—';
  if (typeof value === 'string' && ISO_DATE.test(value)) {
    return value.includes('T') ? formatDateTime(value) : formatDate(value);
  }
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export interface FieldChange {
  key: string;
  label: string;
  before: string;
  after: string;
  changed: boolean;
}

/**
 * Every field either side mentions, old and new value side by side.
 *
 * `changed` rather than filtering: the summary wants only what moved, the
 * expanded view wants everything with the moved fields marked.
 */
export function fieldChanges(before: Json | null, after: Json | null): FieldChange[] {
  const keys = [...new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})])];
  return keys.map((key) => {
    const old = before && key in before ? formatValue(before[key]) : '—';
    const next = after && key in after ? formatValue(after[key]) : '—';
    return {
      key,
      label: fieldLabel(key),
      before: old,
      after: next,
      // A field only one side carries is context (the tenant a licence belongs
      // to), not a change — unless the entry is a creation or a deletion.
      changed: before !== null && after !== null && key in before && key in after && old !== next,
    };
  });
}

function changedText(before: Json | null, after: Json | null, skip: string[] = []): string {
  return fieldChanges(before, after)
    .filter((change) => change.changed && !skip.includes(change.key))
    .map((change) => `${change.label}: ${change.before} → ${change.after}`)
    .join(' · ');
}

const SYNC_COUNTERS: [string, string][] = [
  ['tenants_created', 'Mandanten neu'],
  ['users_created', 'Benutzer neu'],
  ['users_updated', 'Benutzer aktualisiert'],
  ['users_tenant_changed', 'Mandantenwechsel'],
  ['users_deactivated', 'Benutzer deaktiviert'],
  ['directory_tenants_created', 'Mandanten neu (Verzeichnis)'],
  ['directory_users_created', 'Benutzer neu (Verzeichnis)'],
  ['directory_users_reactivated', 'reaktiviert (Verzeichnis)'],
  ['directory_users_deactivated', 'deaktiviert (Verzeichnis)'],
];

function syncText(stats: Json): { text: string; problem: boolean } {
  const parts: string[] = [];
  const seen = stats.directory_tenants_seen;
  if (typeof seen === 'number') parts.push(`${seen} Mandanten gelesen`);
  for (const [key, label] of SYNC_COUNTERS) {
    const value = stats[key];
    if (typeof value === 'number' && value > 0) parts.push(`${value} ${label}`);
  }
  const failed = Array.isArray(stats.directory_failed_tenants)
    ? stats.directory_failed_tenants.map(String)
    : [];
  if (failed.length) parts.push(`nicht lesbar: ${failed.join(', ')}`);
  if (parts.length === (typeof seen === 'number' ? 1 : 0)) parts.push('keine Änderungen');
  return { text: parts.join(' · '), problem: failed.length > 0 };
}

function str(json: Json | null, key: string): string | null {
  const value = json?.[key];
  return typeof value === 'string' && value ? value : null;
}

export interface AuditDescription {
  label: string;
  tone: AuditTone;
  /** Who or what the entry is about — shown first, in bold. */
  subject: string | null;
  text: string;
  /** Something went wrong in a way the reader should notice. */
  problem: boolean;
}

export function describe(entry: AuditEntry): AuditDescription {
  const { action, beforeJson: before, afterJson: after } = entry;
  const either = after ?? before;
  const { label, tone } = actionInfo(action);
  const base = { label, tone, problem: false };

  switch (action) {
    case 'user.tenant_changed':
      return {
        ...base,
        subject: entry.entityLabel,
        text: `${formatValue(before?.tenant)} → ${formatValue(after?.tenant)}`,
      };
    case 'user.updated':
      return {
        ...base,
        subject: entry.entityLabel ?? str(after, 'username'),
        text: changedText(before, after) || 'SEP hat nichts geändert',
      };
    case 'user.assigned': {
      const linked = after?.created_in_sep === false ? ' · war in SEP bereits zugewiesen' : '';
      return {
        ...base,
        subject: entry.entityLabel,
        text: `SEP-ID ${formatValue(after?.sep_user_id)} · Rolle ${formatValue(after?.sep_role)} · Mandant ${formatValue(after?.tenant)}${linked}`,
      };
    }
    case 'user.kickout_requested':
    case 'user.kickout':
      return {
        ...base,
        subject: entry.entityLabel ?? str(before, 'username'),
        text: `Grund: «${formatValue(after?.reason)}»`,
      };
    case 'license.enabled':
    case 'license.disabled':
    case 'license.deleted': {
      const rest = changedText(before, after, ['is_enabled']);
      return {
        ...base,
        subject: [str(either, 'tenant'), str(either, 'app')].filter(Boolean).join(' · ') || null,
        text: rest || (before === null ? 'neu angelegt' : ''),
      };
    }
    case 'override.created':
    case 'override.updated':
    case 'override.deleted': {
      const effect = EFFECTS[String(either?.effect)] ?? formatValue(either?.effect);
      const until = either?.expires_at ? ` bis ${formatValue(either.expires_at)}` : '';
      const reason = str(either, 'reason');
      return {
        ...base,
        subject: [str(either, 'user'), str(either, 'app')].filter(Boolean).join(' · ') || null,
        text: `${effect}${until}${reason ? ` — «${reason}»` : ''}`,
      };
    }
    case 'licenses.copied_from_production':
      return {
        ...base,
        subject: str(after, 'target'),
        text: `${formatValue(after?.created)} neu · ${formatValue(after?.updated)} geändert · ${formatValue(after?.deleted)} entfernt`,
      };
    case 'credential.created':
    case 'credential.rotated':
    case 'credential.deleted':
      return { ...base, subject: str(either, 'key'), text: '' };
    case 'sync.completed': {
      const { text, problem } = syncText(after ?? {});
      return { ...base, subject: null, text, problem };
    }
    default:
      return {
        ...base,
        subject: entry.entityLabel,
        text:
          changedText(before, after) ||
          fieldChanges(before, after)
            .map((change) => `${change.label}: ${change.after === '—' ? change.before : change.after}`)
            .join(' · '),
      };
  }
}
